import { triggerGlobalEvent } from '@luna/events';

/**
 * Wraps a payload creator function with API state event triggers.
 * This higher-order function adds global event dispatches for API state changes.
 *
 * @template T - An object type containing a payloadCreator function and a name string.
 *
 * @param {T} config - The configuration object.
 * @param {string} config.name - A unique identifier for the API call.
 * @param {function} config.payloadCreator - The original async function to be wrapped.
 *
 * @returns {T} A new configuration object with the wrapped payloadCreator function.
 *
 * @example
 * const { payloadCreator: queryPage } = withApiStateEvents({
 *   name: 'paymentOrders/orders',
 *   payloadCreator: async ({ pageSize = 1000, pageNumber = 0, ...params }) => {
 *     // Actual API call logic here
 *     return request<PaymentListResponse>(getUrl({ page: pageNumber, size: pageSize, ...params }));
 *   },
 * });
 *
 * // Using the wrapped payloadCreator
 * const result = await queryPage({ pageSize: 50, pageNumber: 1 });
 *
 * @remarks
 * - This function wraps the original payloadCreator to dispatch global events for API state changes.
 * - Events are dispatched for three states: PENDING, FULFILLED, and REJECTED.
 * - Each API call is assigned a unique Symbol reference to distinguish between concurrent calls.
 * - Events are dispatched asynchronously in the next event loop to ensure state updates are completed.
 * - The 'api:state' event is used, which can be listened to for tracking API call states across the application.
 *
 * @see {@link triggerGlobalEvent} for details on how the events are dispatched.
 */
export function withApiStateEvents<
    T extends { payloadCreator: (...args: any[]) => Promise<any>; name: string },
>(config: T): T {
    const { name, payloadCreator } = config;

    return {
        ...config,
        payloadCreator: async (...args: never[]) => {
            const ref = Symbol('ApiStateEventRef');

            // we trigger the event in the next event loop, so state is already updated back then
            setTimeout(() => {
                triggerGlobalEvent('api:state', {
                    name,
                    state: 'PENDING',
                    ref,
                });
            }, 0);

            try {
                const data = await payloadCreator(...args);

                setTimeout(() => {
                    triggerGlobalEvent('api:state', {
                        name,
                        data,
                        state: 'FULFILLED',
                        ref,
                    });
                }, 0);

                return data;
            } catch (error) {
                setTimeout(() => {
                    triggerGlobalEvent('api:state', {
                        name,
                        error: error as Error,
                        state: 'REJECTED',
                        ref,
                    });
                }, 0);

                throw error;
            }
        },
    } as typeof config;
}
